/**
 * Utility functions for generating consistent Firebase Storage paths
 * 
 * Storage structure: data/<language_name>/<grade>/<chapter_id>/<activity_name>/<file_uid>
 */

/**
 * Generate storage path for chapter sentences
 * @param {Object} params
 * @param {string} params.language - Language code (e.g., 'kannada', 'english')
 * @param {string} params.grade - Grade level (e.g., 'grade1', 'grade2')
 * @param {string} params.chapterId - Chapter ID
 * @param {string} params.sentenceId - Sentence ID
 * @param {string} params.fileType - 'image' or 'audio'
 * @param {string} params.variant - Variant type (e.g., 'language', 'english', 'original')
 * @param {string} params.fileName - Original filename (for extension)
 * @returns {string} Storage path
 */
export function generateSentenceStoragePath({
  language,
  grade,
  chapterId,
  sentenceId,
  fileType,
  variant,
  fileName
}) {
  const ext = (fileName?.split('.').pop() || '').toLowerCase();
  const uid = crypto.randomUUID();
  const safeExt = fileType === 'image' ? (ext || 'jpg') : (ext || 'mp3');
  
  return `data/${language}/${grade}/${chapterId}/sentences/${sentenceId}/${fileType}/${variant}/${uid}.${safeExt}`;
}

/**
 * Generate storage path for activity files
 * @param {Object} params
 * @param {string} params.language - Language code (e.g., 'kannada', 'english')
 * @param {string} params.grade - Grade level (e.g., 'grade1', 'grade2')
 * @param {string} params.chapterId - Chapter ID
 * @param {string} params.activityName - Activity name (e.g., 'word_meaning', 'opposites')
 * @param {string} params.fileName - Original filename (for extension)
 * @param {string} [params.itemId] - Optional item ID for organizing files
 * @returns {string} Storage path
 */
export function generateActivityStoragePath({
  language,
  grade,
  chapterId,
  activityName,
  fileName,
  itemId
}) {
  const ext = (fileName?.split('.').pop() || '').toLowerCase();
  const uid = crypto.randomUUID();
  const safeExt = ext || 'jpg';
  
  // If itemId is provided, include it in the path for better organization
  const itemPath = itemId ? `${itemId}/` : '';
  
  return `data/${language}/${grade}/${chapterId}/${activityName}/${itemPath}${uid}.${safeExt}`;
}

/**
 * Generate storage path for chapter content files
 * @param {Object} params
 * @param {string} params.language - Language code (e.g., 'kannada', 'english')
 * @param {string} params.grade - Grade level (e.g., 'grade1', 'grade2')
 * @param {string} params.chapterId - Chapter ID
 * @param {string} params.fileType - Type of file (e.g., 'cover', 'background')
 * @param {string} params.fileName - Original filename (for extension)
 * @returns {string} Storage path
 */
export function generateChapterStoragePath({
  language,
  grade,
  chapterId,
  fileType,
  fileName
}) {
  const ext = (fileName?.split('.').pop() || '').toLowerCase();
  const uid = crypto.randomUUID();
  const safeExt = ext || 'jpg';
  
  return `data/${language}/${grade}/${chapterId}/${fileType}/${uid}.${safeExt}`;
}

/**
 * Parse grade from chapter object
 * @param {Object} chapter - Chapter object with grade property
 * @returns {string} Formatted grade (e.g., 'grade1')
 */
export function formatGrade(chapter) {
  if (!chapter?.grade) return 'grade1';
  const grade = chapter.grade.toString().toLowerCase();
  return grade.startsWith('grade') ? grade : `grade${grade}`;
}

/**
 * Parse language from chapter object
 * @param {Object} chapter - Chapter object with subject property
 * @returns {string} Language name (e.g., 'kannada', 'english')
 */
export function getLanguageFromSubject(chapter) {
  if (!chapter?.subject) return 'kannada';
  const subject = chapter.subject.toLowerCase();
  
  // Map subjects to languages
  const languageMap = {
    'kannada': 'kannada',
    'english': 'english',
    'hindi': 'hindi',
    'mathematics': 'mathematics',
    'science': 'science',
    'social': 'social'
  };
  
  return languageMap[subject] || subject;
}
